import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useFiltros } from '../lib/filtros.jsx';
import { entero } from '../lib/formato.js';
import { COLOR_TKT, txt, TXT_TKT } from '../lib/enums.js';
import { Cargando, Error as AvisoError, Vacio } from '../piezas/Estados.jsx';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { useMask, useSesion } from '../piezas/Sesion.jsx';

interface Tablero {
  kpis: {
    abiertos: number;
    en_riesgo: number;
    cerrados_mes: number;
    visitas_falsas_mes: number;
    sitios: number;
    idc_activos: number;
  };
  por_estatus: { estado: string; n: number }[];
  contratos: {
    folio: string;
    cliente: string;
    volumen?: number;
    consumido: number;
    disponible: number;
    agotado: boolean;
    consumido_pct: number;
  }[];
  por_mes: { mes: string; servicios: number; terminados: number }[];
  inventario: { almacen: string; piezas: number; valor_centavos?: number; moneda?: string }[];
}

/**
 * Dona por estatus, dibujada a mano en SVG.
 *
 * Sin libreria de graficas a proposito: son cuatro lineas de trigonometria,
 * los colores salen del mismo mapa que las pastillas de la tabla, y una
 * dependencia de 300 KB para esto no se justifica.
 */
function Dona({
  datos,
  total,
}: {
  datos: { estado: string; n: number }[];
  total: number;
}): JSX.Element {
  const R = 52;
  const GROSOR = 18;
  let acumulado = 0;

  return (
    <div className="fila" style={{ gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg
        width={140}
        height={140}
        viewBox="0 0 140 140"
        role="img"
        aria-label={`Distribucion de ${total} tickets por estatus`}
      >
        <g transform="translate(70,70)">
          {datos.map((d) => {
            const fraccion = d.n / Math.max(total, 1);
            /* El arco se dibuja con el borde del circulo y stroke-dasharray:
               una vuelta completa mide 2*pi*R, asi que el largo del segmento
               es esa circunferencia por la fraccion. */
            const circunferencia = 2 * Math.PI * R;
            const largo = circunferencia * fraccion;
            const desfase = circunferencia * acumulado;
            acumulado += fraccion;
            return (
              <circle
                key={d.estado}
                r={R}
                fill="none"
                stroke={COLOR_TKT[d.estado] ?? 'var(--txt-3)'}
                strokeWidth={GROSOR}
                strokeDasharray={`${largo} ${circunferencia - largo}`}
                strokeDashoffset={-desfase}
                /* -90 grados para que el primer segmento arranque arriba. */
                transform="rotate(-90)"
              />
            );
          })}
          <text textAnchor="middle" y={-2} fontSize={24} fontWeight={700} fill="var(--txt)">
            {total}
          </text>
          <text textAnchor="middle" y={15} fontSize={10} fill="var(--txt-3)">
            TICKETS
          </text>
        </g>
      </svg>

      <div style={{ flex: 1, minWidth: 180 }}>
        {datos.map((d) => (
          <div key={d.estado} className="fila" style={{ gap: 8, padding: '3px 0' }}>
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: COLOR_TKT[d.estado] ?? 'var(--txt-3)',
                flex: '0 0 10px',
              }}
            />
            <span className="crece" style={{ fontSize: 12.5 }}>
              {txt(TXT_TKT, d.estado)}
            </span>
            <b style={{ fontSize: 12.5 }}>{d.n}</b>
            <span className="muted" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>
              {((100 * d.n) / Math.max(total, 1)).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Barras de servicios por mes, con el valor escrito encima. */
function Barras({
  datos,
}: {
  datos: { mes: string; servicios: number; terminados: number }[];
}): JSX.Element {
  const max = Math.max(...datos.map((d) => d.servicios), 1);

  return (
    <div className="fila" style={{ alignItems: 'flex-end', gap: 10, height: 150 }}>
      {datos.map((d) => {
        const alto = (d.servicios / max) * 110;
        const altoTerminados = (d.terminados / max) * 110;
        return (
          <div key={d.mes} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 3 }}>{d.servicios}</div>
            <div
              style={{
                position: 'relative',
                height: alto,
                minHeight: 2,
                borderRadius: 4,
                background: 'color-mix(in srgb, var(--brand) 22%, transparent)',
              }}
            >
              {/* Los terminados se dibujan DENTRO de la barra del total, no al
                  lado: es una parte del total, no otra serie. */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: altoTerminados,
                  borderRadius: 4,
                  background: 'var(--brand)',
                }}
              />
            </div>
            <div className="muted" style={{ fontSize: 10.5, marginTop: 5 }}>
              {d.mes.slice(5)}/{d.mes.slice(2, 4)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Tablero(): JSX.Element {
  const [datos, setDatos] = useState<Tablero | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { veMontos } = useSesion();
  const mask = useMask();
  const { consulta } = useFiltros();

  useEffect(() => {
    /* `consulta` en las dependencias: al cambiar un filtro el resumen se
       vuelve a pedir. Se pide al servidor y no se recorta aqui, porque los
       numeros salen ya agregados de la base y recortarlos en el navegador
       daria totales que no cuadran con sus partes. */
    setDatos(null);
    setError(null);
    api
      .obtener<Tablero>(`/v1/tablero${consulta}`)
      .then(({ datos: d }) => setDatos(d))
      .catch(setError);
  }, [consulta]);

  if (error !== null)
    return (
      <>
        <Pagehead />
        <AvisoError error={error} />
      </>
    );
  if (!datos)
    return (
      <>
        <Pagehead />
        <Cargando que="el resumen" />
      </>
    );

  const { kpis } = datos;
  const totalEstatus = datos.por_estatus.reduce((a, x) => a + x.n, 0);

  return (
    <>
      <Pagehead />

      <div className="grid kpis" data-prueba="kpis">
        <div className="card kpi">
          <span className="ico" aria-hidden="true">
            🎫
          </span>
          <span className="val">
            <span>Servicios abiertos</span>
            <b data-prueba="kpi-abiertos">{entero(kpis.abiertos)}</b>
          </span>
        </div>
        <div className="card kpi">
          <span
            className="ico"
            aria-hidden="true"
            style={{ background: 'color-mix(in srgb, var(--bad) 14%, transparent)' }}
          >
            ⚠
          </span>
          <span className="val">
            <span>En riesgo de SLA</span>
            <b style={{ color: kpis.en_riesgo > 0 ? 'var(--bad-ac)' : undefined }}>
              {entero(kpis.en_riesgo)}
            </b>
          </span>
        </div>
        <div className="card kpi">
          <span
            className="ico"
            aria-hidden="true"
            style={{ background: 'color-mix(in srgb, var(--ok) 14%, transparent)' }}
          >
            ✓
          </span>
          <span className="val">
            <span>Cerrados este mes</span>
            <b>{entero(kpis.cerrados_mes)}</b>
          </span>
        </div>
        <div className="card kpi">
          <span
            className="ico"
            aria-hidden="true"
            style={{ background: 'color-mix(in srgb, var(--warn) 16%, transparent)' }}
          >
            🚫
          </span>
          <span className="val">
            <span>Visitas en falso del mes</span>
            <b>{entero(kpis.visitas_falsas_mes)}</b>
          </span>
        </div>
        <div className="card kpi">
          <span className="ico" aria-hidden="true">
            📍
          </span>
          <span className="val">
            <span>Sitios en padron</span>
            <b>{entero(kpis.sitios)}</b>
          </span>
        </div>
        <div className="card kpi">
          <span className="ico" aria-hidden="true">
            🤝
          </span>
          <span className="val">
            <span>IDC activos</span>
            <b>{entero(kpis.idc_activos)}</b>
          </span>
        </div>
      </div>

      <div className="grid r2" style={{ marginTop: 14 }}>
        <div className="card">
          <div className="card-h">
            <h2>Tickets por estatus</h2>
          </div>
          {totalEstatus === 0 ? (
            <Vacio mensaje="Todavia no hay tickets" icono="🎫" />
          ) : (
            <Dona datos={datos.por_estatus} total={totalEstatus} />
          )}
        </div>

        <div className="card">
          <div className="card-h">
            <h2>Servicios por mes</h2>
            <span className="muted" style={{ fontSize: 11, marginLeft: 'auto' }}>
              Relleno sólido: terminados
            </span>
          </div>
          <Barras datos={datos.por_mes} />
        </div>
      </div>

      <div className="sec-title">Saldo contratado</div>
      <div className="card p0">
        {datos.contratos.length === 0 ? (
          <Vacio mensaje="No hay contratos vigentes" icono="📄" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Contrato</th>
                <th>Cliente</th>
                <th className="num">Volumen</th>
                <th className="num">Consumido</th>
                <th className="num">Disponible</th>
                <th style={{ width: 160 }}>Avance</th>
              </tr>
            </thead>
            <tbody>
              {datos.contratos.map((c) => (
                <tr key={c.folio}>
                  <td className="folio">{c.folio}</td>
                  <td>{c.cliente}</td>
                  <td className="num">{entero(c.volumen)}</td>
                  <td className="num">{entero(c.consumido)}</td>
                  <td className="num">
                    {/* El disponible negativo se muestra tal cual: truncarlo
                        en cero esconde el excedente que se va a facturar. */}
                    <span style={{ color: c.disponible < 0 ? 'var(--bad-ac)' : undefined }}>
                      {entero(c.disponible)}
                    </span>
                  </td>
                  <td>
                    <div className="fila" style={{ gap: 8 }}>
                      <div
                        className={
                          c.agotado
                            ? 'bar bad crece'
                            : c.consumido_pct >= 85
                              ? 'bar warn crece'
                              : 'bar crece'
                        }
                      >
                        <i style={{ width: `${Math.min(c.consumido_pct, 100)}%` }} />
                      </div>
                      <span className="muted" style={{ fontSize: 11, minWidth: 38 }}>
                        {c.consumido_pct}%
                      </span>
                    </div>
                    {c.agotado && (
                      <span className="tag t-hot" style={{ marginTop: 4 }}>
                        Agotado · el excedente va on demand
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* El bloque de valor de inventario solo existe para quien ve montos.
          No se dibuja vacio con guiones: una tarjeta de puros "—" solo
          informa que hay algo que no se puede ver. */}
      {veMontos && datos.inventario.length > 0 && (
        <>
          <div className="sec-title">Valor del inventario por almacen</div>
          <div className="card p0">
            <table>
              <thead>
                <tr>
                  <th>Almacen</th>
                  <th className="num">Piezas</th>
                  <th className="num">Valor</th>
                </tr>
              </thead>
              <tbody>
                {datos.inventario.map((i) => (
                  <tr key={i.almacen}>
                    <td>{i.almacen}</td>
                    <td className="num">{entero(i.piezas)}</td>
                    <td className="num">{mask(i.valor_centavos, i.moneda)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
